import React, { useState } from 'react';
import { Stack, Typography, Button, useTheme, Menu, MenuItem, ListItemIcon, IconButton } from '@mui/material';
import { Product } from '@domain/product/models/Product';
import { Label } from '@shared/ui/common';
import { MoreVert, Delete as DeleteIcon, Undo } from '@mui/icons-material';
import { useProductUpdate } from '@features/products/hooks/useProductUpdate';

interface ProductInfoHeaderProps {
  product: Product;
  showFullDescription: boolean;
  onToggleDescription: () => void;
}

enum ProductKind {
  PURCHASABLE = 'purchasable',
  ASSEMBLED = 'assembled',
}

const ProductInfoHeader: React.FC<ProductInfoHeaderProps> = ({
  product,
  showFullDescription,
  onToggleDescription,
}) => {
  const theme = useTheme();
  const { updateProduct } = useProductUpdate();
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handlePurchasableKindChange = (value: string) => {
    updateProduct({
      productId: product.id,
      updateData: {
        purchasable: value === ProductKind.PURCHASABLE,
      },
    });
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleArchiveProduct = () => {
    updateProduct({
      productId: product.id,
      updateData: {
        active: false,
      },
    });
  };

  const handleRestoreProduct = () => {
    updateProduct({
      productId: product.id,
      updateData: {
        active: true,
      },
    });
  };

  return (

    <Stack direction="column" spacing={4}>


      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>

        <Stack direction="row" alignItems="center" spacing={2} p={1} borderRadius={2} border={1} borderColor={theme.palette.divider}>
          <Typography variant="subtitle2" color="text.primary">Type</Typography>

          <Label
            options={[
              { value: ProductKind.PURCHASABLE, label: 'Purchasable', color: theme.palette.success.light },
              { value: ProductKind.ASSEMBLED, label: 'Assembled', color: theme.palette.info.light },
            ]}
            currentValue={product.purchasable ? ProductKind.PURCHASABLE : ProductKind.ASSEMBLED}
            onOptionChanged={handlePurchasableKindChange}
          />


        </Stack>

        <IconButton onClick={(e) => setMoreMenuAnchorEl(e.currentTarget)}>
          <MoreVert />
        </IconButton>

        <Menu
          anchorEl={moreMenuAnchorEl}
          open={Boolean(moreMenuAnchorEl)}
          onClose={() => handleMoreMenuClose()}
        >

          {product.active === false ? (
            <MenuItem onClick={() => handleRestoreProduct()} color="success" >
              <ListItemIcon>
                <Undo color="success" />
              </ListItemIcon>
              <Typography variant="subtitle2" color="success">Restore Product</Typography>
            </MenuItem>
          ) : (
            <MenuItem onClick={() => handleArchiveProduct()} color="error" >
              <ListItemIcon>
                <DeleteIcon color="error" />
              </ListItemIcon>
              <Typography variant="subtitle2" color="error">Archive Product</Typography>
            </MenuItem>
          )}
        </Menu>
      </Stack>

      {/* 
      <Stack direction="column" spacing={1}>
        <Typography variant="subtitle2" color="text.primary">Title</Typography>
        <Typography variant="body2" color="text.secondary">{product.title}</Typography>
      </Stack> */}

      <Stack direction="column" alignItems="flex-start" spacing={0}>

        <Typography variant="subtitle2" color="text.primary">Description</Typography>

        <Typography
          variant="body2"
          component="div"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: showFullDescription ? 'none' : 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: product.description || 'No description available' }} />
        </Typography>

        {product.description && product.description.length > 200 && (
          <Button
            size="small"
            onClick={onToggleDescription}
            sx={{ mt: 1 }}
          >
            {showFullDescription ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </Stack>
    </Stack >

  );
};

export default ProductInfoHeader;
